import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TILES = ["🐶", "🐱", "🦊", "🐼", "🦁", "🐸", "🐵", "🐨", "🐧", "🦄", "🐙", "🐢"];

export default function KidsLogin() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState("code"); // code -> pick -> passcode
  const [code, setCode] = useState("");
  const [kids, setKids] = useState([]);
  const [picked, setPicked] = useState(null);
  const [passcode, setPasscode] = useState([]);
  const [loading, setLoading] = useState(false);

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/kid/lookup", { family_code: code.toUpperCase() });
      setKids(data.children);
      setStep("pick");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't find that family");
    } finally { setLoading(false); }
  };

  const tapTile = (t) => {
    if (passcode.length >= 4) return;
    const next = [...passcode, t];
    setPasscode(next);
    if (next.length === 4) submitPasscode(next);
  };

  const clearPasscode = () => setPasscode([]);

  const submitPasscode = async (pc) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/kid/login", { child_id: picked.user_id, picture_passcode: pc });
      localStorage.setItem("token", data.token);
      setUser(data.user);
      toast.success(`Hi ${data.user.name}! 👋`);
      nav("/practice");
    } catch (err) {
      toast.error("Wrong passcode — try again");
      setPasscode([]);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4 py-10 bg-[var(--sun)]">
      <div className="nb-card p-8 sm:p-10 w-full max-w-lg bg-white">
        <div className="text-center">
          <Sparkles className="w-10 h-10 mx-auto text-[var(--coral)]" />
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-2">Kid mode 🎒</h1>
          <p className="text-neutral-600 mt-1">
            {step === "code" && "Type your family's 4-letter code."}
            {step === "pick" && "Which one is you?"}
            {step === "passcode" && "Tap your 4 special pictures — in order."}
          </p>
        </div>

        {step === "code" && (
          <form onSubmit={submitCode} className="space-y-4 mt-6">
            <input
              data-testid="kid-code"
              className="nb-input text-center text-3xl tracking-widest font-extrabold uppercase"
              placeholder="ABCD"
              value={code}
              maxLength={4}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
              required
            />
            <button data-testid="kid-code-submit" disabled={loading || code.length !== 4} className="nb-btn w-full text-lg py-3">
              {loading ? "Looking…" : <>Next <ArrowRight className="w-5 h-5" /></>}
            </button>
          </form>
        )}

        {step === "pick" && (
          <div className="space-y-3 mt-6">
            {kids.map((k) => (
              <button
                key={k.user_id}
                data-testid={`kid-pick-${k.user_id}`}
                onClick={() => { setPicked(k); setStep("passcode"); }}
                className="w-full p-4 border-2 border-black rounded-xl bg-white hover:bg-[var(--bg-highlight)] flex items-center gap-3 text-left"
              >
                <span className="text-4xl">{k.avatar || "🧑‍🎓"}</span>
                <span className="text-xl font-extrabold">{k.name}</span>
              </button>
            ))}
            <button onClick={() => { setStep("code"); setKids([]); }} className="text-sm underline text-neutral-500 w-full text-center pt-2">← Back</button>
          </div>
        )}

        {step === "passcode" && picked && (
          <div className="mt-6">
            <div className="flex items-center gap-2 justify-center mb-3">
              <span className="text-3xl">{picked.avatar}</span>
              <span className="font-extrabold text-xl">{picked.name}</span>
            </div>
            <div className="flex gap-2 justify-center mb-4" data-testid="passcode-progress">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`w-14 h-14 border-2 border-black rounded-xl text-3xl flex items-center justify-center ${passcode[i] ? "bg-[var(--mint)]" : "bg-white"}`}>
                  {passcode[i] || ""}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-3">
              {TILES.map((t) => (
                <button
                  key={t}
                  data-testid={`kid-tile-${t}`}
                  onClick={() => tapTile(t)}
                  disabled={loading}
                  className="aspect-square text-4xl border-2 border-black rounded-xl bg-white hover:bg-[var(--sun)] active:translate-y-1 active:shadow-none shadow-[3px_3px_0_0_#1C1C1C] transition-all"
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-4 text-sm">
              <button onClick={clearPasscode} className="underline text-neutral-500">Clear</button>
              <button onClick={() => { setStep("pick"); setPasscode([]); setPicked(null); }} className="underline text-neutral-500">← Someone else</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
